import React, { createContext, useContext, useState } from 'react';
import { STANDARD_RELAX, LOUNGE_RELAX } from '../components/BusSeatMap';

const BookingContext = createContext();

export const PASSENGER_CATEGORIES = [
  { id: 'adult', discount: 0 },
  { id: 'senior', discount: 0.1 },
  { id: 'youth', discount: 0.1 },
  { id: 'child', discount: 0.4 },
  { id: 'preschool', discount: 0.6 },
];

export const useBooking = () => {
  const context = useContext(BookingContext);
  if (!context) throw new Error('useBooking must be used within BookingProvider');
  return context;
};

export const BookingProvider = ({ children }) => {
  const [searchParams, setSearchParams] = useState({
    from: '',
    to: '',
    departureDate: new Date().toISOString().split('T')[0],
    returnDate: '',
    passengers: { adult: 1, senior: 0, youth: 0, child: 0, preschool: 0 },
    promoCode: '',
  });

  const [selectedTrip, setSelectedTrip] = useState(null);
  const [selectedReturnTrip, setSelectedReturnTrip] = useState(null);
  const [passengerInfo, setPassengerInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });
  const [currentStep, setCurrentStep] = useState(1);

  // Passengers list with category and seat assignment
  // e.g. [{ id: 1, category: 'adult', seat: null }, ...]
  const [passengersList, setPassengersList] = useState([]);
  const [seatAssignments, setSeatAssignments] = useState({}); // { passengerId: seatNumber }

  const totalPassengers = () => {
    const p = searchParams.passengers;
    return p.adult + p.senior + p.youth + p.child + p.preschool;
  };

  const buildPassengersList = () => {
    const list = [];
    let id = 1;
    const p = searchParams.passengers;
    const cats = ['adult', 'senior', 'youth', 'child', 'preschool'];
    cats.forEach((cat) => {
      for (let i = 0; i < p[cat]; i++) {
        list.push({ id, category: cat, seat: null });
        id++;
      }
    });
    return list;
  };

  const RELAX_SURCHARGE = 8; // euros extra for Relax seats

  const getPassengerPrice = (category, basePrice, isRelaxSeat = false) => {
    const cat = PASSENGER_CATEGORIES.find((c) => c.id === category);
    const discount = cat ? cat.discount : 0;
    const price = basePrice * (1 - discount) + (isRelaxSeat ? RELAX_SURCHARGE : 0);
    return parseFloat(price.toFixed(2));
  };

  const totalPrice = () => {
    let total = 0;
    const list = passengersList.length > 0 ? passengersList : buildPassengersList();
    const isLounge = selectedTrip?.type === 'LUX Lounge';
    const relaxSeats = isLounge ? LOUNGE_RELAX : STANDARD_RELAX;
    list.forEach((psg) => {
      const seat = seatAssignments[psg.id];
      const isRelax = seat ? relaxSeats.has(seat) : false;
      if (selectedTrip) total += getPassengerPrice(psg.category, selectedTrip.price, isRelax);
      if (selectedReturnTrip) total += getPassengerPrice(psg.category, selectedReturnTrip.price, isRelax);
    });
    return total.toFixed(2);
  };

  return (
    <BookingContext.Provider
      value={{
        searchParams,
        setSearchParams,
        selectedTrip,
        setSelectedTrip,
        selectedReturnTrip,
        setSelectedReturnTrip,
        passengerInfo,
        setPassengerInfo,
        currentStep,
        setCurrentStep,
        totalPrice,
        totalPassengers,
        passengersList,
        setPassengersList,
        seatAssignments,
        setSeatAssignments,
        buildPassengersList,
        getPassengerPrice,
      }}
    >
      {children}
    </BookingContext.Provider>
  );
};
